import { Result, err, ok } from "neverthrow"
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import { DashboardLoadItemStatus, expToDraftTodo, StatusedAppointment, StatusedTodo, WorkItemType } from "@fusion-advantage/formlib"
import { ZodType } from "zod"
import { CloudToDeviceApptBody, CloudToDeviceTodoBody, ZCloudToDeviceApptBody, ZCloudToDeviceTodoBody } from "../redux/syncthunk/ZCloudToDevice"
import { CloudToDeviceBlobType, selectCloudToDeviceBlob } from "../dal/ldbCloudToDeviceBlob"
import { deleteSyncedDeviceToCloudBlobs, DeviceToCloudEncounterStatus, selectDeviceToCloudEncounterStatusChanges } from "../dal/ldbDeviceToCloudBlob"
import { CodeNext, safeParseJson } from "@fusion-advantage/backcompat"
import { mustSelectExport } from "./selectExport"
import { ApptEncounterGuidSelector } from "./ApptEncounterGuidSelector"

type SyncedWorkItems<T> = {
	body: T
	syncedAt: number
}

type SelectSyncedWorkItemsResult<T> = Result<SyncedWorkItems<T>, FusionError>

type SelectTodosResultOk = {
	WorkItems: StatusedTodo[]
}

type SelectApptsResultOk = Omit<CloudToDeviceApptBody, "Appointments"> & {
	Appointments: StatusedAppointment[]
}

// TODO
// it seems like here we should really pay attention to how we are
// suppressing, displaying error messages to the user

export class WorkItemSelector {
	private upn: string
	private rTodo: SelectSyncedWorkItemsResult<CloudToDeviceTodoBody>
	private rAppt: SelectSyncedWorkItemsResult<CloudToDeviceApptBody>
	private apptEncounterGuidSelector: ApptEncounterGuidSelector
	private statusChanges: Map<string, DeviceToCloudEncounterStatus>

	private constructor(
		upn: string,
		rTodo: SelectSyncedWorkItemsResult<CloudToDeviceTodoBody>,
		rAppt: SelectSyncedWorkItemsResult<CloudToDeviceApptBody>,
		apptEncounterGuidSelector: ApptEncounterGuidSelector,
		statusChanges: Map<string, DeviceToCloudEncounterStatus>,
	) {
		this.upn = upn
		this.rTodo = rTodo
		this.rAppt = rAppt
		this.apptEncounterGuidSelector = apptEncounterGuidSelector
		this.statusChanges = statusChanges
	}

	private static async selectBody<T>(upn: string, type: WorkItemType, schema: ZodType<T>): Promise<SelectSyncedWorkItemsResult<T>> {
		let rSelect = await selectCloudToDeviceBlob({
			type: CloudToDeviceBlobType.ApiResponse,
			id: type,
			upn,
		})
		if(rSelect.isErr()) {
			return err(serializeFusionError('WorkItemSelector.selectBody', rSelect.error))
		}
		if(!rSelect.value) {
			return err((serializeFusionError('WorkItemSelector.selectBody', `expected select type '${type}' but null`)))
		}
		let rParse = safeParseJson(rSelect.value.data, schema)
		if(rParse.isErr()) {
			return err(serializeFusionError('WorkItemSelector.selectBody', rParse.error))
		}
		return ok({
			body: rParse.value,
			syncedAt: rSelect.value.syncedAt,
		})
	}

	static async create(upn: string): Promise<Result<WorkItemSelector, FusionError>> {
		// as has been proven recently with appointments down in dev,
		// it may actually be useful to be able to use todos even if appts
		// are down, or vice versa...
		const rTodo = await WorkItemSelector.selectBody(upn, WorkItemType.Todo, ZCloudToDeviceTodoBody)
		const rAppt = await WorkItemSelector.selectBody(upn, WorkItemType.Appointment, ZCloudToDeviceApptBody)
		// clean up presync status changes that we definitely have
		// newer data for
		if(rTodo.isOk() && rAppt.isOk()) {
			const createdBefore = Math.min(rTodo.value.syncedAt, rAppt.value.syncedAt)
			const rDelete = await deleteSyncedDeviceToCloudBlobs(upn, createdBefore)
			if(rDelete.isErr()) {
				return err(serializeFusionError('WorkItemSelector.create', rDelete.error))
			}
		}
		const rChanges = await selectDeviceToCloudEncounterStatusChanges(upn)
		if(rChanges.isErr()) {
			return err(serializeFusionError('WorkItemSelector.create', rChanges.error))
		}
		const apptEncounterGuidSelector = await ApptEncounterGuidSelector.create(upn)
		const selector = new WorkItemSelector(upn, rTodo, rAppt, apptEncounterGuidSelector, rChanges.value)
		return ok(selector)
	}

	async selectTodos(): Promise<Result<SelectTodosResultOk, FusionError>> {
		if(this.rTodo.isErr()) {
			return err(serializeFusionError('WorkItemSelector.selectTodos', this.rTodo.error))
		}
		const existingTodos = this.rTodo.value.body.WorkItems
		const existingEncounterGuids = existingTodos.map(todo => todo.EncounterGUID)
		let todos: StatusedTodo[] = []
		// surface placeholder Todo for presynced started encounter
		for(let [encounterGuid, {nextCodeNext}] of this.statusChanges.entries()) {
			if(nextCodeNext !== CodeNext.InProgress || existingEncounterGuids.includes(encounterGuid)) {
				continue
			}
			const rExp = await mustSelectExport(this.upn, encounterGuid)
			if(rExp.isErr()) {
				todos.push({
					type: WorkItemType.Todo,
					status: DashboardLoadItemStatus.Error,
					itemKey: {
						type: WorkItemType.Todo,
						encounterGuid,
					},
					error: rExp.error,
				})
			} else {
				todos.push({
					type: WorkItemType.Todo,
					status: DashboardLoadItemStatus.Ready,
					item: expToDraftTodo(rExp.value.exp),
				})
			}
		}
		const syncedAt = this.rTodo.value.syncedAt
		todos.push(...this.rTodo.value.body.WorkItems.filter(workItem => {
			const statusChange = this.statusChanges.get(workItem.EncounterGUID)
			if(statusChange && statusChange.createdAt > syncedAt) {
				switch(statusChange.nextCodeNext) {
					// for presynced Todos that have moved to these statuses,
					// do not show the Todo
					case CodeNext.Cancelled:
					case CodeNext.Completed:
					case CodeNext.Level1Audit:
					case CodeNext.ClinicianAuditReview:
					case CodeNext.Audit1ToComplete:
					case CodeNext.PostSubmitFormAudit:
						return false
				}
			}
			// surface any other synced Todo
			return true
		}).map((todo): StatusedTodo => ({
			type: WorkItemType.Todo,
			item: todo,
			status: DashboardLoadItemStatus.Ready,
		})))
		return ok({
			WorkItems: todos,
		})
	}

	async selectAppts(): Promise<Result<SelectApptsResultOk, FusionError>> {
		if(this.rAppt.isErr()) {
			return err(serializeFusionError('WorkItemSelector.selectAppts', this.rAppt.error))
		}
		const existingAppts = this.rAppt.value.body.Appointments
		const syncedAt = this.rAppt.value.syncedAt
		let appts = existingAppts.map((appt): StatusedAppointment => {
			const rEncounterGuid = this.apptEncounterGuidSelector.mustGet(appt.ServiceAppointmentID)
			if(rEncounterGuid.isErr()) {
				return {
					type: WorkItemType.Appointment,
					status: DashboardLoadItemStatus.Error,
					item: appt,
					error: rEncounterGuid.error,
				}
			}
			const encounterGuid = rEncounterGuid.value
			const appt2: StatusedAppointment = {
				type: WorkItemType.Appointment,
				status: DashboardLoadItemStatus.Ready,
				item: appt,
				encounterGuid,
			}
			const statusChange = this.statusChanges.get(encounterGuid)
			if(statusChange && statusChange.createdAt > syncedAt) {
				switch(statusChange.nextCodeNext) {
					case CodeNext.Cancelled:
						appt2.status = DashboardLoadItemStatus.Cancelled
						break
				}
			}
			return appt2
		})
		return ok({
			...this.rAppt.value.body,
			Appointments: appts,
		})
	}
}
